import type { Doc } from "building-types/doc";
import type { DocEntityBase } from "building-types/entities/docEntityBase";
import type { CompositeLevelHandle, DocEntityHandle } from "building-types/handles/docEntityHandleTypes";
import type { CompositeLevel } from "building-types/levels/CompositeLevel";
import type { LevelDef } from "building-types/levels/LevelDef";
import type { ParameterRef } from "building-types/options/parameter";
import { type ViewportCameraKey, viewportCameraKeys } from "building-types/views/CameraKey";
import type {
  AnnotationDrawingTemplate,
  GenericDrawingTemplate,
  ViewportViewType,
} from "building-types/views/planSets";
import type { ElevationView, SectionView } from "building-types/views/View3D";
import type { Table } from "common/data/table";
import type { WorldInches, SheetInches } from "common/types/nominalNumbersTypes";
import { ensureDefined } from "common/utils/assertOfType";
import { assertUnreachable } from "common/utils/assertUnreachable";
import { numSub, numAdd, worldInchesToSheet, sheetInchesToWorld } from "common/utils/nominalNumbers";
import objectKeyFilter from "common/utils/objectKeyFilter";
import { sortEntitiesBySortIndex } from "common/utils/sortIndex";
import { camelCaseToTitleCase } from "common/utils/strings";
import { isParameterRef } from "doc/parameterTools";
import { isAnnotationDrawingTemplate, isGenericDrawingTemplate } from "./contentTemplateTools";
import {
  instanceIdForComponent,
  resolveInstanceScopedEntityId,
  resolveInstanceScopedEntityIds,
} from "./entity/componentInstanceEntityResolve";
import { docEntityTypeToDocTableName } from "./entity/docEntityTypeToDocTableName";
import docGetEntity from "./entity/docGetEntity";

type DocContentSourceTables = Pick<
  Doc,
  "elevationViews" | "levels" | "sectionViews" | "contentTemplates" | "compositeLevels"
>;

function docGetViewportContentSourceTable(
  doc: DocContentSourceTables,
  viewType: Exclude<ViewportViewType, "VIEW_3D">,
):
  | Table<LevelDef>
  | Table<ElevationView>
  | Table<SectionView>
  | Table<GenericDrawingTemplate | AnnotationDrawingTemplate> {
  switch (viewType) {
    case "ELEVATION_VIEW":
    case "SECTION_VIEW":
      return doc[ensureDefined(docEntityTypeToDocTableName[viewType])];
    case "PLAN_VIEW":
      return doc.levels;
    case "DRAWING":
      return objectKeyFilter(
        doc.contentTemplates,
        (key) =>
          isGenericDrawingTemplate(doc.contentTemplates[key]) || isAnnotationDrawingTemplate(doc.contentTemplates[key]),
      ) as Table<GenericDrawingTemplate | AnnotationDrawingTemplate>;
    default:
      assertUnreachable(viewType);
  }
}

export function docGetViewportContentSourceHandle(
  doc: Doc,
  viewType: ViewportViewType,
  id: string | ParameterRef,
): DocEntityHandle | undefined {
  if (isParameterRef(id)) {
    return undefined;
  }

  let handle: DocEntityHandle | undefined;

  switch (viewType) {
    case "ELEVATION_VIEW":
    case "SECTION_VIEW":
      handle = { type: viewType, id };
      break;
    case "PLAN_VIEW":
      // Check if this is a composite level first, then fall back to regular level
      if (doc.compositeLevels?.[id]) {
        handle = { type: "COMPOSITE_LEVEL", id };
      } else {
        handle = { type: "LEVEL", id };
      }
      break;
    case "DRAWING":
      handle = { type: "CONTENT_TEMPLATE", id };
      break;
    default:
      break;
  }

  return docGetEntity(doc, handle) ? handle : undefined;
}

/**
 * Checks if a content source ID refers to a composite level
 */
export function isCompositeLevelContentSourceId(doc: Pick<Doc, "compositeLevels">, id: string): boolean {
  return doc.compositeLevels?.[id] !== undefined;
}

/**
 * Checks if a content source ID refers to a composite level with multiple members.
 * Single-member composites are treated like their member level for plan rendering.
 */
export function isMultiMemberCompositeLevelContentSourceId(doc: Pick<Doc, "compositeLevels">, id: string): boolean {
  const compositeLevel = doc.compositeLevels?.[id];
  return compositeLevel !== undefined && compositeLevel.members.length > 1;
}

/**
 * Gets the composite level entity from the doc by ID
 */
export function docGetCompositeLevel(doc: Doc, id: string): CompositeLevel | undefined {
  return doc.compositeLevels?.[id];
}

/**
 * Gets the composite level handle if the ID refers to a composite level
 */
export function docGetCompositeLevelHandle(doc: Doc, id: string): CompositeLevelHandle | undefined {
  if (doc.compositeLevels?.[id]) {
    return { type: "COMPOSITE_LEVEL", id };
  }
  return undefined;
}

/**
 * Resolves a composite level to its member level IDs.
 * Returns the individual level IDs that make up the composite.
 * When a levels table is supplied, instance-scoped member ids fall back to the base entity id
 * when the compound id is not present (for example replication component instance levels).
 */
export function resolveCompositeLevelToLevelIds(compositeLevel: CompositeLevel, levels?: Table<unknown>): string[] {
  if (!levels) {
    return resolveInstanceScopedEntityIds(compositeLevel.members);
  }

  const levelIds = new Set<string>();
  for (const member of compositeLevel.members) {
    const resolvedLevelId = resolveInstanceScopedEntityId({ path: member, table: levels });
    if (resolvedLevelId) {
      levelIds.add(resolvedLevelId);
    }
  }
  return Array.from(levelIds);
}

/**
 * Resolves a content source ID to level IDs.
 * If the ID refers to a composite level, returns all member level IDs.
 * If the ID refers to a level in the supplied level table, returns that level ID in an array.
 */
export function resolveContentSourceIdToLevelIds(
  doc: Pick<Doc, "compositeLevels" | "levels">,
  contentSourceId: string,
  levels: Table<unknown> | undefined = doc.levels,
): string[] {
  const compositeLevel = doc.compositeLevels?.[contentSourceId];
  if (compositeLevel) {
    return resolveCompositeLevelToLevelIds(compositeLevel, levels);
  }
  // Level content sources resolve to the matching entry in the supplied level table.
  if (levels?.[contentSourceId]) {
    return [contentSourceId];
  }
  return [];
}

export function viewportContentSourceRenderId(
  contentSourceId: string,
  componentInstanceChain?: readonly string[],
): string {
  return componentInstanceChain && componentInstanceChain.length > 0
    ? instanceIdForComponent(contentSourceId, componentInstanceChain)
    : contentSourceId;
}

export function viewportContentSourceRenamer(
  id: string,
  viewType: ViewportViewType,
  doc: DocContentSourceTables,
  defaultName: string = "",
): string {
  if (viewType === "VIEW_3D") {
    return camelCaseToTitleCase(id);
  }

  // For PLAN_VIEW, also check composite levels
  if (viewType === "PLAN_VIEW" && doc.compositeLevels?.[id]) {
    return doc.compositeLevels[id].name;
  }

  return docGetViewportContentSourceTable(doc, viewType)[id]?.name ?? defaultName;
}

export function docGetViewportContentSourceValues(
  doc: DocContentSourceTables,
  viewType: ViewportViewType,
): readonly ViewportCameraKey[] | (LevelDef | CompositeLevel)[] | ElevationView[] | SectionView[] {
  // 3D views do not store camera information, so we just use the built-in camera keys
  if (viewType === "VIEW_3D") {
    return viewportCameraKeys;
  }

  // For PLAN_VIEW, include both levels and composite levels
  if (viewType === "PLAN_VIEW") {
    const levels = sortEntitiesBySortIndex(Object.values(doc.levels));
    const compositeLevels = sortEntitiesBySortIndex(Object.values(doc.compositeLevels ?? {}));
    return [...levels, ...compositeLevels];
  }

  return sortEntitiesBySortIndex(Object.values(docGetViewportContentSourceTable(doc, viewType)));
}

export function docGetViewportContentSourceKeys(doc: DocContentSourceTables, viewType: ViewportViewType): string[] {
  return docGetViewportContentSourceValues(doc, viewType).map((entity: string | DocEntityBase) =>
    typeof entity === "string" ? entity : entity.id,
  );
}

export function updateViewportWorldOriginValue(
  newPan: WorldInches,
  oldPan: WorldInches,
  scale: number,
  oldWorldOrigin: SheetInches,
): SheetInches {
  const panDelta = numSub(newPan, oldPan);
  return numAdd(oldWorldOrigin, worldInchesToSheet(panDelta, scale));
}

export function computeViewportPanValue(
  worldOriginInPageSpace: SheetInches,
  viewportOrigin: SheetInches,
  scale: number,
): WorldInches {
  return sheetInchesToWorld(numSub(worldOriginInPageSpace, viewportOrigin), scale);
}
